import {
  Conversation,
  Message,
  CreateConversationData,
  SendMessageData,
  RecommendedQuestionsResponse,
  HomeRecommendedQuestionsResponse,
} from '../../schemas/chat';
import type { PaginatedResponse } from '../../schemas/common';
import api from '../../lib/api';
import type { AxiosResponse } from '../../lib/api';
import { streamMessage, StreamMessageRequest } from '../../lib/stream';

// Chat API with generic type support
export const chatAPI = {
  getConversations: (params?: {
    page?: number;
    page_size?: number;
    include_archived?: boolean;
    include_pinned?: boolean;
    folder_id?: string;
    search?: string;
  }): Promise<AxiosResponse<PaginatedResponse<Conversation>>> =>
    api.get<PaginatedResponse<Conversation>>('/api/v1/chats/', { params }),

  getConversation: (id: string): Promise<AxiosResponse<Conversation>> =>
    api.get<Conversation>(`/api/v1/chats/${id}`),

  createConversation: (data: CreateConversationData): Promise<AxiosResponse<Conversation>> =>
    api.post<Conversation>('/api/v1/chats/', data),

  updateConversation: (
    id: string,
    data: Partial<CreateConversationData>
  ): Promise<AxiosResponse<Conversation>> => api.put<Conversation>(`/api/v1/chats/${id}`, data),

  deleteConversation: (id: string): Promise<AxiosResponse<void>> =>
    api.delete<void>(`/api/v1/chats/${id}`),

  getMessages: (conversationId: string): Promise<AxiosResponse<Message[]>> =>
    api.get<Message[]>(`/api/v1/chats/${conversationId}/messages`),

  sendMessage: (
    conversationId: string,
    message: SendMessageData
  ): Promise<AxiosResponse<Message>> =>
    api.post<Message>(`/api/v1/chats/${conversationId}/messages`, message),

  deleteMessage: (messageId: string): Promise<AxiosResponse<void>> =>
    api.delete<void>(`/messages/${messageId}`),

  // Extended conversation management
  archiveConversation: (id: string): Promise<AxiosResponse<Conversation>> =>
    api.put<Conversation>(`/api/v1/chats/${id}`, { is_archived: true }),

  pinConversation: (id: string, pinned: boolean): Promise<AxiosResponse<Conversation>> =>
    api.put<Conversation>(`/api/v1/chats/${id}`, { is_pinned: pinned }),

  regenerateMessage: (conversationId: string, messageId: string): Promise<AxiosResponse<Message>> =>
    api.post<Message>(`/api/v1/chats/${conversationId}/messages/${messageId}/regenerate`),

  streamMessage: async <T extends StreamMessageRequest = StreamMessageRequest>(
    conversationId: string,
    data: T
  ): Promise<Response> => {
    // Use Fetch API for streaming (imported from stream.ts)
    return streamMessage(conversationId, data);
  },

  // Tags and folders management
  addTagToConversation: (id: string, tag: string): Promise<AxiosResponse<Conversation>> =>
    api.put<Conversation>(`/api/v1/chats/${id}`, { tags: tag }),

  removeTagFromConversation: (id: string, tag: string): Promise<AxiosResponse<Conversation>> =>
    api.put<Conversation>(`/api/v1/chats/${id}`, { remove_tag: tag }),

  moveConversationToFolder: (id: string, folderId?: string): Promise<AxiosResponse<Conversation>> =>
    api.put<Conversation>(`/api/v1/chats/${id}`, { folder_id: folderId }),

  // Share chat functionality
  shareChat: (
    chatId: string,
    isPublic: boolean
  ): Promise<
    AxiosResponse<{
      chat_id: string;
      share_id: string | null;
      is_public: boolean;
      share_url: string | null;
    }>
  > =>
    api.post<{
      chat_id: string;
      share_id: string | null;
      is_public: boolean;
      share_url: string | null;
    }>(`/api/v1/chats/${chatId}/share`, { is_public: isPublic }),

  getSharedChat: (shareId: string): Promise<AxiosResponse<Conversation>> =>
    api.get<Conversation>(`/api/v1/chats/shared/${shareId}`),

  // Streaming endpoints
  streamChat: (conversationId: string, data: SendMessageData): Promise<AxiosResponse<Message>> => {
    return api.post<Message>(`/api/v1/chats/${conversationId}/stream`, data, {
      responseType: 'stream',
    } as any);
  },

  // Recommended questions
  getRecommendedQuestions: (
    conversationId: string
  ): Promise<AxiosResponse<RecommendedQuestionsResponse>> =>
    api.get<RecommendedQuestionsResponse>(`/api/v1/chats/${conversationId}/recommended-questions`),

  // Home page recommended questions
  getHomeRecommendedQuestions: (): Promise<AxiosResponse<HomeRecommendedQuestionsResponse>> =>
    api.get<HomeRecommendedQuestionsResponse>('/api/v1/chats/recommended-questions'),
};
